import apiClient from './api';
import {
  CanvaChannel,
  ListDesignsParams,
  ListDesignsResponse,
  CanvaImportRequest,
  StartImportResponse,
  CanvaImportJobStatus,
} from '@/types/canva';

// Shape returned by the label PATCH endpoint
interface UpdatedChannelResponse {
  id: number;
  platformUsername: string;
  metadata: CanvaChannel['metadata'];
  isActive: boolean;
}

/**
 * Canva API Service
 *
 * Handles all API calls related to Canva integration:
 * - Fetching connected Canva channels
 * - Listing designs from Canva
 * - Starting design import jobs
 * - Monitoring import job status
 * - Cancelling import jobs
 */

// ============================================================================
// API SERVICE
// ============================================================================

export const canvaService = {
  /**
   * Get connected Canva channels for the team.
   * By default returns only active channels. Pass `{ includeInactive: true }`
   * to also surface disconnected/revoked channels (e.g. reconnect prompts).
   */
  getChannels: async (opts?: { includeInactive?: boolean }): Promise<CanvaChannel[]> => {
    const params = opts?.includeInactive ? { includeInactive: 'true' } : undefined;
    const response = await apiClient.get('/api/auth/canva/channels', { params });
    return response.data.data;
  },

  /**
   * List designs from Canva
   */
  listDesigns: async (params: ListDesignsParams): Promise<ListDesignsResponse> => {
    const response = await apiClient.get('/api/auth/canva/designs', { params });
    return response.data.data;
  },

  /**
   * Start a Canva import job
   */
  startImport: async (request: CanvaImportRequest): Promise<StartImportResponse> => {
    const response = await apiClient.post('/api/auth/canva/import', request);
    return response.data.data;
  },

  /**
   * Get import job status
   */
  getImportStatus: async (jobId: number): Promise<CanvaImportJobStatus> => {
    const response = await apiClient.get(`/api/auth/canva/import/${jobId}`);
    return response.data.data;
  },

  /**
   * Cancel an import job
   */
  cancelImport: async (jobId: number): Promise<void> => {
    await apiClient.post(`/api/auth/canva/import/${jobId}/cancel`);
  },

  /**
   * Update the user-editable label for a Canva channel.
   * Passing an empty string clears the label.
   */
  updateChannelLabel: async (
    channelId: number,
    label: string
  ): Promise<UpdatedChannelResponse> => {
    const response = await apiClient.patch(
      `/api/auth/canva/channels/${channelId}/label`,
      { label }
    );
    return response.data.data;
  },
};

export default canvaService;
